// src/pages/Lieferscheine.jsx
// Lieferscheine an einer Wartung (Anna-Lena 21.09.2026, Migration 66).
//   Buero:   PDF hochladen, ansehen, loeschen.
//   Monteur: ansehen und vom Kunden auf dem Handy unterschreiben lassen.
// Die Logik (Geraetespeicher, Stempeln, Senden) steht in lib/lieferscheine.js.

import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { C, Field, TextInput } from './formui';
import SignaturCanvas from './SignaturCanvas';
import { t } from '../lib/sprache';
import {
  lieferscheineLaden, originaleVorladen, lieferscheinBlob, lieferscheinHochladen,
  lieferscheinLoeschen, lieferscheinUnterschreiben, beiLieferscheinAenderung,
  starteLieferscheinAutomatik, wartendeFehler,
} from '../lib/lieferscheine';
import { pdfOeffnen, seiteZeichnen, unterschriftsstelleFinden } from '../lib/pdfAnzeige';

const datumText = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const z = (n) => String(n).padStart(2, '0');
  return z(d.getDate()) + '.' + z(d.getMonth() + 1) + '.' + d.getFullYear() + ', ' + z(d.getHours()) + ':' + z(d.getMinutes());
};

const knopf = (farbe, hell) => ({
  minHeight: 42, padding: '8px 14px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer',
  border: hell ? '1.5px solid ' + farbe : 'none', background: hell ? C.white : farbe, color: hell ? farbe : C.white,
  fontFamily: 'inherit', touchAction: 'manipulation',
});

// darfHochladen: Buero/Admin. darfUnterschreiben: offenes Protokoll (Monteur und Buero).
export default function Lieferscheine({ protokollId, stationId, darfHochladen = false, darfUnterschreiben = true, userId }) {
  const [liste, setListe] = useState([]);
  const [ohneNetz, setOhneNetz] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [laedtHoch, setLaedtHoch] = useState(false);
  const [unterschrift, setUnterschrift] = useState(null);   // Lieferschein, der gerade unterschrieben wird
  const [rot, setRot] = useState({});                        // id -> Ablehnungsgrund
  const [ansicht, setAnsicht] = useState(null);              // Lieferschein, der angezeigt wird

  const laden = useCallback(async () => {
    if (!protokollId) return;
    const { liste: l, ohneNetz: o } = await lieferscheineLaden(protokollId);
    setListe(l);
    setOhneNetz(o);
    const f = {};
    for (const x of l.filter(y => y.wartetAufNetz)) {
      const grund = await wartendeFehler(x.id);
      if (grund) f[x.id] = grund;
    }
    setRot(f);
    // Mit Netz die Originale ins Geraet holen - dann klappt das Unterschreiben
    // spaeter auch ohne Empfang.
    if (!o && darfUnterschreiben) originaleVorladen(l).catch(() => {});
  }, [protokollId, darfUnterschreiben]);

  useEffect(() => {
    starteLieferscheinAutomatik();
    laden();
    return beiLieferscheinAenderung((d) => { if (!d.protokollId || d.protokollId === protokollId) laden(); });
  }, [laden, protokollId]);

  if (!protokollId) return null;
  // Monteur ohne Lieferscheine: den Abschnitt gar nicht zeigen.
  if (!darfHochladen && liste.length === 0) return null;

  const hochladen = async (datei) => {
    setLaedtHoch(true); setFehler(null);
    try { await lieferscheinHochladen({ protokollId, stationId, datei }); await laden(); }
    catch (e) { setFehler(t('Hochladen fehlgeschlagen: {fehler}', { fehler: e.message || String(e) })); }
    finally { setLaedtHoch(false); }
  };
  // In der App anzeigen statt in neuem Fenster - das verhielt sich je nach Handy anders.
  const oeffnen = (l) => setAnsicht(l);
  const loeschen = async (l) => {
    if (!window.confirm(t('Lieferschein „{name}“ wirklich löschen?', { name: l.datei_name || l.titel || '' }))) return;
    try { await lieferscheinLoeschen(l); await laden(); }
    catch (e) { setFehler(t('Löschen fehlgeschlagen: {fehler}', { fehler: e.message || String(e) })); }
  };

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, marginBottom: 6 }}>{t('Lieferscheine')}</div>
      {ohneNetz && (
        <div style={{ fontSize: 13, color: C.gray500, marginBottom: 6 }}>
          {t('Ohne Netz – es wird der zuletzt geladene Stand gezeigt.')}
        </div>
      )}
      {liste.length === 0 && (
        <div style={{ fontSize: 14, color: C.gray500, marginBottom: 8 }}>{t('Noch kein Lieferschein hinterlegt.')}</div>
      )}
      {liste.map(l => {
        const unterschrieben = !!(l.signiert_pfad || l.wartetAufNetz);
        return (
          <div key={l.id} style={{ background: C.white, border: '1px solid ' + C.gray200, borderRadius: 10, padding: 12, marginBottom: 8,
                                   borderLeft: '5px solid ' + (rot[l.id] ? C.red : unterschrieben ? C.green : C.orange) }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.navy }}>{l.titel || l.datei_name || t('Lieferschein')}</div>
            <div style={{ fontSize: 13, marginTop: 2, color: rot[l.id] ? C.red : unterschrieben ? C.green : C.orangeText || C.orange, fontWeight: 600 }}>
              {rot[l.id]
                ? t('Vom Server abgelehnt: {fehler}', { fehler: rot[l.id] })
                : unterschrieben
                  ? t('Unterschrieben von {name} am {datum}', { name: l.unterschrift_name || '–', datum: datumText(l.unterschrieben_am) })
                    + (l.wartetAufNetz ? ' · ' + t('wartet auf Netz') : '')
                  : t('noch nicht unterschrieben')}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              <button type="button" onClick={() => oeffnen(l)} style={knopf(C.navy, true)}>{t('Ansehen')}</button>
              {darfUnterschreiben && !unterschrieben && (
                <button type="button" onClick={() => setUnterschrift(l)} style={knopf(C.green)}>✍ {t('Vom Kunden unterschreiben lassen')}</button>
              )}
              {darfHochladen && (
                <button type="button" onClick={() => loeschen(l)}
                  style={{ ...knopf(C.red, true), border: 'none', background: 'none', textDecoration: 'underline', fontWeight: 600 }}>
                  {t('löschen')}
                </button>
              )}
            </div>
          </div>
        );
      })}
      {darfHochladen && (
        <label style={{ display: 'inline-block', marginTop: 4, ...knopf(laedtHoch ? C.gray300 : C.navy), cursor: laedtHoch ? 'wait' : 'pointer' }}>
          {laedtHoch ? t('wird hochgeladen …') : '+ ' + t('Lieferschein hochladen (PDF)')}
          <input type="file" accept="application/pdf,.pdf" disabled={laedtHoch} style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) hochladen(f); }}/>
        </label>
      )}
      {fehler && <div style={{ marginTop: 8, fontSize: 14, color: C.red, fontWeight: 600 }}>{fehler}</div>}
      {ansicht && <AnsichtFenster lieferschein={ansicht} onSchliessen={() => setAnsicht(null)}/>}
      {unterschrift && (
        <UnterschriftFenster lieferschein={unterschrift} userId={userId}
          onAbbrechen={() => setUnterschrift(null)}
          onFertig={() => { setUnterschrift(null); laden(); }}/>
      )}
    </div>
  );
}

// --- PDF in der App: Seiten als Bilder, optional mit waehlbarer Unterschriftsstelle ---
// Masse der Unterschrift im PDF (wie in lib/lieferscheine.js).
const U_BREITE = 150;
const U_HOEHE = 44;

function PdfSeiten({ blob, stelle, onStelle, onGefunden }) {
  const [doc, setDoc] = useState(null);
  const [fehler, setFehler] = useState(null);
  const huelle = useRef(null);
  const [breite, setBreite] = useState(0);

  useEffect(() => {
    let aus = false;
    setDoc(null); setFehler(null);
    pdfOeffnen(blob).then(async (d) => {
      if (aus) return;
      setDoc(d);
      if (onGefunden) {
        const s = await unterschriftsstelleFinden(d).catch(() => null);
        if (!aus) onGefunden(s);
      }
    }).catch(e => { if (!aus) setFehler(e.message || String(e)); });
    return () => { aus = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob]);

  useLayoutEffect(() => {
    const messen = () => { if (huelle.current) setBreite(Math.min(huelle.current.clientWidth, 900)); };
    messen();
    window.addEventListener('resize', messen);
    return () => window.removeEventListener('resize', messen);
  }, []);

  return (
    <div ref={huelle} style={{ width: '100%' }}>
      {fehler && <div style={{ color: C.red, fontWeight: 600, padding: 12 }}>{t('Der Lieferschein lässt sich nicht anzeigen: {fehler}', { fehler })}</div>}
      {!doc && !fehler && <div style={{ color: C.gray500, padding: 20, textAlign: 'center' }}>{t('lädt …')}</div>}
      {doc && breite > 0 && Array.from({ length: doc.numPages }, (_, i) => (
        <SeiteBild key={i} doc={doc} nummer={i + 1} breite={breite} stelle={stelle} onStelle={onStelle}/>
      ))}
    </div>
  );
}

function SeiteBild({ doc, nummer, breite, stelle, onStelle }) {
  const leinwand = useRef(null);
  const [ansicht, setAnsicht] = useState(null);   // { viewport, dichte }
  const markerRef = useRef(null);

  useEffect(() => {
    let aus = false;
    if (leinwand.current) {
      seiteZeichnen(doc, nummer, leinwand.current, breite)
        .then(v => { if (!aus) setAnsicht(v); }).catch(() => {});
    }
    return () => { aus = true; };
  }, [doc, nummer, breite]);

  const hier = !!(stelle && stelle.seite === nummer && ansicht);
  let marker = null;
  if (hier) {
    const { viewport, dichte } = ansicht;
    const [vx, vy] = viewport.convertToViewportPoint(stelle.x, stelle.y);
    const m = viewport.scale / dichte;
    marker = { left: vx / dichte, top: vy / dichte - U_HOEHE * m, width: U_BREITE * m, height: U_HOEHE * m };
  }
  // Zur vorgeschlagenen bzw. angetippten Stelle scrollen.
  const markerSchluessel = hier ? stelle.seite + ':' + Math.round(stelle.y) : '';
  useEffect(() => {
    if (markerSchluessel && markerRef.current) markerRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [markerSchluessel]);

  const antippen = (e) => {
    if (!onStelle || !ansicht) return;
    const r = leinwand.current.getBoundingClientRect();
    const { viewport, dichte } = ansicht;
    const [px, py] = viewport.convertToPdfPoint((e.clientX - r.left) * dichte, (e.clientY - r.top) * dichte);
    // Getippt wird AUF die Linie; die Unterschrift steht mittig darueber.
    onStelle({ seite: nummer, x: px - U_BREITE / 2, y: py });
  };

  return (
    <div style={{ position: 'relative', margin: '0 auto 10px', width: 'fit-content', boxShadow: '0 1px 6px rgba(0,0,0,0.18)' }}>
      <canvas ref={leinwand} onClick={antippen} style={{ display: 'block', background: '#FFFFFF', cursor: onStelle ? 'crosshair' : 'default' }}/>
      {marker && (
        <div ref={markerRef} style={{ position: 'absolute', ...marker, border: '2px dashed ' + C.green,
                                     background: 'rgba(26,122,74,0.12)', borderRadius: 4, pointerEvents: 'none',
                                     display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.green, background: 'rgba(255,255,255,0.85)', padding: '0 4px', marginBottom: 2 }}>
            {t('Unterschrift')}
          </span>
        </div>
      )}
    </div>
  );
}

const vollbild = { position: 'fixed', inset: 0, background: C.gray100, zIndex: 300, display: 'flex', flexDirection: 'column' };
const kopfleiste = { background: C.white, borderBottom: '1px solid ' + C.gray200, padding: '10px 12px',
                     display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' };

// Nur ansehen (das unterschriebene, sonst das Original).
function AnsichtFenster({ lieferschein, onSchliessen }) {
  const [blob, setBlob] = useState(null);
  const [fehler, setFehler] = useState(null);
  useEffect(() => {
    lieferscheinBlob(lieferschein).then(setBlob).catch(e => setFehler(e.message || String(e)));
  }, [lieferschein]);
  return (
    <div style={vollbild}>
      <div style={kopfleiste}>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.navy }}>{lieferschein.titel || lieferschein.datei_name}</div>
        <button type="button" onClick={onSchliessen} style={knopf(C.navy, true)}>{t('Schließen')}</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
        {fehler && <div style={{ color: C.red, fontWeight: 600 }}>{t('Der Lieferschein lässt sich nicht anzeigen: {fehler}', { fehler })}</div>}
        {blob && <PdfSeiten blob={blob}/>}
      </div>
    </div>
  );
}

// Unterschreiben in zwei Schritten: 1. Stelle pruefen/antippen, 2. Name + Unterschrift.
function UnterschriftFenster({ lieferschein, userId, onAbbrechen, onFertig }) {
  const [schritt, setSchritt] = useState('stelle');
  const [blob, setBlob] = useState(null);
  const [stelle, setStelle] = useState(null);
  const [gesucht, setGesucht] = useState(false);
  const [name, setName] = useState('');
  const [bild, setBild] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(null);

  useEffect(() => {
    lieferscheinBlob(lieferschein, 'original').then(setBlob).catch(e => setFehler(
      !navigator.onLine ? t('Dieser Lieferschein ist noch nicht auf dem Gerät. Bitte einmal mit Netz das Protokoll öffnen.')
        : (e.message || String(e))));
  }, [lieferschein]);

  const bestaetigen = async () => {
    setLaeuft(true); setFehler(null);
    try {
      await lieferscheinUnterschreiben(lieferschein, { name, unterschrift: bild, userId, position: stelle });
      onFertig();
    } catch (e) {
      setFehler(e.message || String(e));
      setLaeuft(false);
    }
  };

  if (schritt === 'stelle') {
    return (
      <div style={vollbild}>
        <div style={kopfleiste}>
          <div style={{ flex: '1 1 220px' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.navy }}>{t('Wo soll unterschrieben werden?')}</div>
            <div style={{ fontSize: 13, color: C.gray700, marginTop: 2, lineHeight: 1.35 }}>
              {!gesucht ? t('Lieferschein wird geladen …')
                : stelle ? t('Der grüne Rahmen zeigt die Stelle. Stimmt sie nicht, einfach auf die richtige Linie tippen.')
                  : t('Keine Unterschriftszeile gefunden – bitte auf die Linie tippen, auf der unterschrieben wird.')}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onAbbrechen} style={knopf(C.navy, true)}>{t('Abbrechen')}</button>
            <button type="button" onClick={() => setSchritt('unterschrift')} disabled={!stelle}
              style={{ ...knopf(stelle ? C.green : C.gray300), minHeight: 46 }}>{t('Weiter')} ›</button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
          {fehler && <div style={{ color: C.red, fontWeight: 600, padding: 8 }}>{fehler}</div>}
          {blob && <PdfSeiten blob={blob} stelle={stelle} onStelle={setStelle}
                              onGefunden={(s) => { setStelle(s); setGesucht(true); }}/>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', zIndex: 300,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div style={{ background: C.white, borderRadius: 12, padding: 16, width: '100%', maxWidth: 560, maxHeight: '100%', overflowY: 'auto' }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.navy }}>{t('Lieferschein unterschreiben')}</div>
        <div style={{ fontSize: 14, color: C.gray700, margin: '4px 0 12px' }}>
          {lieferschein.titel || lieferschein.datei_name}
        </div>
        <Field label={t('Name des Kunden')}>
          <TextInput value={name} onChange={setName} placeholder={t('Vor- und Nachname')}/>
        </Field>
        <div style={{ fontSize: 13, color: C.gray500, margin: '8px 0 6px' }}>{t('Unterschrift des Kunden')}</div>
        <SignaturCanvas value={bild} onChange={setBild} height={200}/>
        <div style={{ fontSize: 13, color: C.gray500, marginTop: 8, lineHeight: 1.4 }}>
          {t('Die Unterschrift kommt auf die gewählte Linie, Name, Datum und Uhrzeit klein darüber.')}
        </div>
        {fehler && <div style={{ marginTop: 10, fontSize: 14, color: C.red, fontWeight: 600 }}>{fehler}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 14, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setSchritt('stelle')} disabled={laeuft} style={knopf(C.navy, true)}>‹ {t('Stelle ändern')}</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onAbbrechen} disabled={laeuft} style={knopf(C.navy, true)}>{t('Abbrechen')}</button>
            <button type="button" onClick={bestaetigen} disabled={laeuft || !bild || !name.trim()}
              style={{ ...knopf(laeuft || !bild || !name.trim() ? C.gray300 : C.green), minHeight: 48 }}>
              {laeuft ? t('wird gespeichert …') : '✓ ' + t('Unterschrift bestätigen')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
